"use client";

import { ArrowRight } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
function PlateCoordinates() {
  return <>
      <span aria-hidden className="pointer-events-none absolute left-1 top-1 z-10 text-[10px] leading-none text-muted-foreground opacity-40 transition-opacity duration-300 group-hover:opacity-100">+</span>
      <span aria-hidden className="pointer-events-none absolute right-1 top-1 z-10 text-[10px] leading-none text-muted-foreground opacity-40 transition-opacity duration-300 group-hover:opacity-100">+</span>
      <span aria-hidden className="pointer-events-none absolute bottom-1 left-1 z-10 text-[10px] leading-none text-muted-foreground opacity-40 transition-opacity duration-300 group-hover:opacity-100">+</span>
      <span aria-hidden className="pointer-events-none absolute bottom-1 right-1 z-10 text-[10px] leading-none text-muted-foreground opacity-40 transition-opacity duration-300 group-hover:opacity-100">+</span>
    </>;
}
function formatDate(dateStr?: string) {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    }).toUpperCase();
  } catch {
    return "";
  }
}
function CulinaryNotesConsumer() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  const fallbackImages = [{
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/vpn_security_shield.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/vpn_security_shield.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/vpn_security_shield.webp-wvc-sizes"
  }, {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/vpn_global_network.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/vpn_global_network.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/vpn_global_network.webp-wvc-sizes"
  }, {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/vpn_privacy_tech.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/vpn_privacy_tech.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/vpn_privacy_tech.webp-wvc-sizes"
  }];
  if (loading) {
    return <div className="grid grid-cols-1 gap-8 md:grid-cols-3 md:gap-8">
        {[0, 1, 2].map(i => <div data-index={i} key={i} className="flex flex-col">
            <div className="aspect-[4/5] w-full bg-muted/40 animate-pulse" />
            <div className="mt-6 h-px w-full bg-border" />
            <div className="mt-5 h-3 w-32 bg-muted/40 animate-pulse" />
            <div className="mt-4 h-6 w-3/4 bg-muted/40 animate-pulse" />
            <div className="mt-3 h-3 w-24 bg-muted/40 animate-pulse" />
          </div>)}
      </div>;
  }
  if (error) {
    return <p className="font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground">
        Unable to load news at this time.
      </p>;
  }
  if (!posts || posts.length === 0) {
    return <p className="font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground">
        No news published yet.
      </p>;
  }
  return <>
      {}
      <div className="hidden grid-cols-1 gap-8 md:grid md:grid-cols-3 md:gap-8">
        {posts.slice(0, 3).map((post, i) => {
        const fallback = fallbackImages[i % fallbackImages.length];
        const imgSrc = post.featuredImage || fallback.src;
        const category = post.categories && post.categories.length > 0 ? post.categories[0].name.toUpperCase() : "UPDATE";
        return <Link to={post.link || "#"} key={post.id} data-index={i} className="group relative flex flex-col">
              <div className="relative aspect-[4/5] w-full overflow-hidden bg-muted">
                <PlateCoordinates />
                <img src={imgSrc} alt={post.title} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-[600ms] ease-out group-hover:scale-[1.03]" />
              </div>

              <div className="mt-6 h-px w-full bg-border transition-colors duration-300 group-hover:bg-primary" />

              <div className="relative mt-5 flex flex-col p-0">
                <div className="flex items-center gap-3">
                  <span className="inline-flex items-center border border-border px-2.5 py-1 font-mono text-[11px] uppercase tracking-[0.18em] text-foreground">
                    {category}
                  </span>
                </div>

                <h3 className="font-default relative mt-4 text-xl font-normal leading-[1.25] tracking-tight text-foreground md:text-[1.375rem]">
                  <span aria-hidden className="absolute -left-3 top-1/2 h-1 w-1 -translate-y-1/2 translate-x-[-8px] bg-primary opacity-0 transition-all duration-300 group-hover:translate-x-0 group-hover:opacity-100" />
                  {post.title}
                </h3>

                <p className="mt-3 font-mono text-xs uppercase tracking-[0.12em] text-muted-foreground">
                  {formatDate(post.date)}
                </p>
              </div>
            </Link>;
      })}
      </div>

      {}
      <div className="md:hidden -mx-4">
        <div className="flex snap-x snap-mandatory gap-4 overflow-x-auto px-4 pb-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {posts.slice(0, 3).map((post, i) => {
          const fallback = fallbackImages[i % fallbackImages.length];
          const imgSrc = post.featuredImage || fallback.src;
          const category = post.categories && post.categories.length > 0 ? post.categories[0].name.toUpperCase() : "UPDATE";
          return <Link to={post.link || "#"} key={post.id} data-index={i} className="group relative flex w-[85%] shrink-0 snap-start flex-col">
                <div className="relative aspect-[4/5] w-full overflow-hidden bg-muted">
                  <PlateCoordinates />
                  <img src={imgSrc} alt={post.title} loading="lazy" decoding="async" className="h-full w-full object-cover" />
                </div>

                <div className="mt-5 h-px w-full bg-border" />

                <div className="mt-4 flex flex-col">
                  <span className="inline-flex w-fit items-center border border-border px-2.5 py-1 font-mono text-[11px] uppercase tracking-[0.18em] text-foreground">
                    {category}
                  </span>
                  <h3 className="font-default mt-3 text-lg font-normal leading-[1.25] tracking-tight text-foreground">
                    {post.title}
                  </h3>
                  <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.12em] text-muted-foreground">
                    {formatDate(post.date)}
                  </p>
                </div>
              </Link>;
        })}
        </div>

        <div className="mt-2 flex items-center justify-center gap-2">
          {posts.slice(0, 3).map((_, i) => <span data-index={i} key={i} className="h-1 w-1 bg-foreground/30" aria-hidden />)}
        </div>
      </div>
    </>;
}
export default function Insights() {
  const wp_query_params = {
    post_type: "post",
    per_page: 3,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section data-section-id="1336" id="relatedvpninsights" className="relative w-full overflow-hidden bg-background py-32 md:py-44">
      {}
      <span aria-hidden className="font-default pointer-events-none absolute -left-8 top-24 select-none text-[clamp(280px,42vw,620px)] font-thin leading-none tracking-tighter text-transparent opacity-[0.08] md:top-32" style={{
      WebkitTextStroke: "1px hsl(220 12% 7%)"
    }}>
        05
      </span>

      <div className="relative mx-auto w-full max-w-[1440px] px-4 md:px-16">
        {}
        <div className="grid grid-cols-1 gap-10 md:grid-cols-12 md:gap-8">
          <div className="md:col-span-9">
            {}
            <div className="flex items-center gap-4">
              <span className="font-mono text-xs uppercase tracking-[0.22em] text-foreground">
                § 05 — VPN Insights
              </span>
            </div>

            {}
            <div className="mt-6 flex items-center">
              <span className="block h-px w-24 bg-primary" />
              <span className="block h-1 w-1 bg-primary" />
            </div>

            {}
            <h2 className="font-default mt-8 max-w-[16ch] text-[clamp(2.25rem,5.2vw,5.5rem)] font-light leading-[0.98] tracking-tight text-foreground">
              Expert{" "}
              <span className="font-serif italic font-light">security</span>{" "}
              insights daily.
            </h2>
          </div>

          <div className="flex items-end md:col-span-3 md:justify-end">
            <Link to="/BlogsPage" className="group inline-flex items-center">
              <Button variant="link" className="font-mono text-xs uppercase tracking-[0.18em] text-foreground hover:no-underline p-0 h-auto">
                <span className="relative">
                  View All News
                  <span aria-hidden className="absolute -bottom-1 left-0 h-px w-0 bg-foreground transition-all duration-[350ms] ease-out group-hover:w-full" />
                </span>
                <ArrowRight className="ml-2 size-3.5 transition-transform duration-300 group-hover:translate-x-0.5" />
              </Button>
            </Link>
          </div>
        </div>

        {}
        <div className="relative mt-20 md:mt-28">
          <PostsProvider wp_query={{
          post_type: "post",
          per_page: 3,
          order: "desc",
          orderby: "date"
        }}>
            <CulinaryNotesConsumer />
          </PostsProvider>
        </div>

        {}
        <div className="mt-20 h-px w-full bg-border md:mt-28" />
      </div>
    </section>;
}